import { SHORTCUT_ACTIONS_BY_ID } from '@/lib/shortcuts/shortcutActions';
import { BUILTIN_DICTATION_PHRASES, normalizeVoicePhrase, RESERVED_COMMAND_PREFIXES, } from '@/lib/stt/dictation-commands';
/**
 * Built-in spoken phrases for the read-flow viewer actions. Every action here
 * reuses the exact handler the keyboard shortcut uses (ViewerShell's
 * ShortcutHandlers map) so voice adds zero new behavior. Deliberately excluded:
 * anything modal, destructive, or that navigates away (open report, next/prev
 * study, back, layout switches, annotation clearing) - reachable via custom
 * bindings if a reader opts in, never by default.
 */
export const DEFAULT_VIEWER_VOICE_BINDINGS = {
    'next image': 'nav.nextImage',
    'previous image': 'nav.prevImage',
    'first image': 'nav.firstImage',
    'last image': 'nav.lastImage',
    'capture image': 'report.capture',
    'save to report': 'report.capture',
    'reset view': 'transform.reset',
    invert: 'transform.invert',
    'invert image': 'transform.invert',
    'flip horizontal': 'transform.flipHorizontal',
    'flip vertical': 'transform.flipVertical',
    'rotate clockwise': 'transform.rotate',
    'rotate counterclockwise': 'transform.rotateCcw',
    'play cine': 'playback.cine',
    'stop cine': 'playback.cine',
};
/**
 * Every phrase the built-in voice surface owns: the dictation grammar plus the
 * default viewer bindings. Custom bindings must not collide with any of these.
 * Mirrored server-side in PutUserPreferences.cs (BuiltinDictationPhrases);
 * viewer-voice-commands.test.ts pins this list so the mirror cannot drift
 * without a failing test on whichever side changed.
 */
export const BUILTIN_VOICE_COMMAND_PHRASES = [
    ...new Set([...BUILTIN_DICTATION_PHRASES, ...Object.keys(DEFAULT_VIEWER_VOICE_BINDINGS)]),
];
/**
 * Matches a final STT segment against viewer voice bindings. Whole-segment
 * only: the phrase must be the entire (normalized) utterance, mirroring how
 * bare section names jump, so viewer commands never fire on words inside a
 * dictated sentence.
 */
export function matchViewerCommand(segment, bindings) {
    const normalized = normalizeVoicePhrase(segment);
    if (!normalized)
        return null;
    return bindings[normalized] ?? null;
}
/** Server-mirrored shape guard for binding actions (see PutUserPreferences.cs). */
const VOICE_BINDING_ACTION_PATTERN = /^(viewer|macro|section):[A-Za-z0-9_./-]{1,64}$/;
export const MAX_VOICE_BINDINGS = 20;
const MIN_VOICE_BINDING_PHRASE_LENGTH = 2;
export const MAX_VOICE_BINDING_PHRASE_LENGTH = 40;
const MAX_VOICE_BINDING_PHRASE_WORDS = 5;
/** Splits a stored binding action into kind + target, or null when malformed. */
export function parseVoiceBindingAction(action) {
    if (!VOICE_BINDING_ACTION_PATTERN.test(action))
        return null;
    const separator = action.indexOf(':');
    return {
        kind: action.slice(0, separator),
        target: action.slice(separator + 1),
    };
}
/**
 * Client-side mirror of the server's phrase rules (PutUserPreferences.cs) so
 * the settings card can reject a bad row inline instead of surfacing a 400.
 */
export function validateVoiceBindingPhrase(phrase, existingPhrases) {
    const trimmed = phrase.trim();
    if (/[\r\n]/.test(phrase))
        return 'newline';
    if (trimmed.length < MIN_VOICE_BINDING_PHRASE_LENGTH)
        return 'tooShort';
    if (trimmed.length > MAX_VOICE_BINDING_PHRASE_LENGTH)
        return 'tooLong';
    if (trimmed.split(/\s+/).length > MAX_VOICE_BINDING_PHRASE_WORDS)
        return 'tooManyWords';
    const normalized = normalizeVoicePhrase(trimmed);
    if (BUILTIN_VOICE_COMMAND_PHRASES.includes(normalized) ||
        RESERVED_COMMAND_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
        return 'reserved';
    }
    if (existingPhrases.some((existing) => normalizeVoicePhrase(existing) === normalized)) {
        return 'duplicate';
    }
    return null;
}
/**
 * Merges the default viewer bindings with the user's custom viewer:* rows into
 * the phrase map useDictation and the help popover consume. Built-ins win on
 * collision (the server rejects colliding phrases anyway) and rows whose
 * target is not a real ShortcutActionId are dropped.
 */
export function buildViewerVoiceBindings(custom) {
    const merged = { ...DEFAULT_VIEWER_VOICE_BINDINGS };
    for (const binding of custom ?? []) {
        const parsed = parseVoiceBindingAction(binding.action);
        if (parsed?.kind !== 'viewer')
            continue;
        if (!(parsed.target in SHORTCUT_ACTIONS_BY_ID))
            continue;
        const phrase = normalizeVoicePhrase(binding.phrase);
        if (!phrase || phrase in merged)
            continue;
        merged[phrase] = parsed.target;
    }
    return merged;
}
